/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */


/**
 * ------------------------------------------------------------------------------------------------------------
 * SYNC VARIANT SELECTION WHEN CLICKING PRODUCT MEDIA / CAROUSEL / THUMBNAILS
 * ------------------------------------------------------------------------------------------------------------
 *
 * By default, clicking through the product image carousel (main image, thumbnails, or dot navigation) only
 * changes which image is shown -- it does not select the matching variant, so the displayed price can stay on
 * whatever variant was selected first. This relies on a small JSON block (rendered via a "Custom Liquid" block
 * on the product template) that maps each variant's featured image id to its variant id.
 *
 * Two mechanisms are used together so this is resilient to how the carousel implements navigation internally:
 *
 * 1) A MutationObserver watches for the carousel's own "is-selected" class being applied to a different media
 *    item (this class is what the theme uses to mark the current slide) and selects the matching variant
 *    whenever it changes -- this works no matter how the slide change was triggered (click, swipe, arrow,
 *    dot, or thumbnail), since it reacts to the resulting DOM state rather than the triggering event.
 * 2) A capture-phase click listener on "document" as an immediate fallback, in case the carousel library
 *    does not update the "is-selected" class on navigation.
 */
document.addEventListener('DOMContentLoaded', function () {
  var mapScript = document.querySelector('[data-media-variant-map]');

  if (!mapScript) {
    return;
  }

  var pairs;

  try {
    pairs = JSON.parse(mapScript.textContent);
  } catch (e) {
    return;
  }

  if (!pairs || !pairs.length) {
    return;
  }

  var mediaIdToVariantId = {};

  pairs.forEach(function (pair) {
    mediaIdToVariantId[String(pair[0])] = pair[1];
  });

  document.querySelectorAll('product-media').forEach(function (mediaElement) {
    var mediaList = mediaElement.querySelector('.product__media-list');

    if (!mediaList) {
      return;
    }

    var productContainer = mediaElement.closest('.product') || document;
    var variantsElement = productContainer.querySelector('product-variants') || document.querySelector('product-variants');

    if (!variantsElement || typeof variantsElement.selectVariant !== 'function') {
      return;
    }

    var lastVariantId = null;

    function selectVariantForMediaId(mediaId) {
      var variantId = mediaIdToVariantId[String(mediaId)];

      if (variantId && variantId !== lastVariantId) {
        lastVariantId = variantId;
        variantsElement.selectVariant(parseInt(variantId, 10));
      }
    }

    function checkSelectedMedia() {
      var selectedItem = mediaList.querySelector('.product__media-item.is-selected') || mediaElement.querySelector('.product__media-item.is-selected');

      if (selectedItem) {
        selectVariantForMediaId(selectedItem.getAttribute('data-media-id'));
      }
    }

    new MutationObserver(checkSelectedMedia).observe(mediaElement, {
      attributes: true,
      attributeFilter: ['class'],
      subtree: true
    });

    mediaElement.addEventListener('click', function (event) {
      var item = event.target.closest('[data-media-id]');

      if (item) {
        selectVariantForMediaId(item.getAttribute('data-media-id'));
      }

      setTimeout(checkSelectedMedia, 50);
      setTimeout(checkSelectedMedia, 250);
    }, true);
  });
});


/**
 * ------------------------------------------------------------------------------------------------------------
 * SHOW THE SELECTED VARIANT NAME IN THE PRODUCT TITLE
 * ------------------------------------------------------------------------------------------------------------
 *
 * Appends the selected variant's title (e.g. cover name) to the main product heading, so it's clear which
 * offering is currently selected: "The Grid Wars: Chapter One Graphic Novel - The Eric Canete Limited Edition
 * Variant". Uses the theme's built-in "variant:changed" event, which already fires reliably whenever a variant
 * is selected through the buttons/swatches below the image.
 */
document.addEventListener('variant:changed', function (event) {
  var variant = event.detail && event.detail.variant;

  if (!variant) {
    return;
  }

  var form = event.target;
  var container = (form && form.closest) ? form.closest('.product') : null;
  var titleElement = (container || document).querySelector('[data-product-title]');

  if (!titleElement) {
    return;
  }

  var baseTitle = titleElement.getAttribute('data-base-title');

  if (variant.title && variant.title !== 'Default Title') {
    titleElement.textContent = baseTitle + ' - ' + variant.title;
  } else {
    titleElement.textContent = baseTitle;
  }
});
